<template>
  <div class="post-applicant-readonly">
    <div v-if="data">
      <div
        class="application-record"
      >
        <CollapseContent
          v-if="data.operation_records && data.operation_records.length"
          class="operation-records"
          :title="$t('questionnaire.label.jobApplicationRecord')"
        >
          <div class="application-header">
            <div class="meta-item">
              <span class="meta-label">{{ $t(`${i18nPrefix}.label.applicationFormInvitationTime`) }}</span>
              <span>{{ lastInviteOperateRecord.operation_time|formatDate }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">{{ $t(`${i18nPrefix}.label.applicationFormFinishedTime`) }}</span>
              <span v-if="data.finished_time">{{ data.finished_time|formatDate }}</span>
              <span v-else>-</span>
            </div>
          </div>
          <div
            v-for="record in getOperationRecordList(data)"
            :key="record.id"
            class="operation-record"
          >
            <div class="operation-info">
              <div class="operator">{{ record.operator_name || candidateName }}</div>
              <div class="candidate">
                <span class="label">{{ $t(`${i18nPrefix}.label.applicationFormCandidateName`) }}</span>
                <span class="content">{{ candidateName }}</span>
              </div>
              <div class="status">
                <span class="label">{{ $t(`${i18nPrefix}.label.applicationFormStatus`) }}</span>
                <span class="content">{{ getOperationStatusChange(data, record) }}</span>
              </div>
              <div v-if="record.reason" class="reason">
                <span class="label">{{ $t(`${i18nPrefix}.label.applicationFormReactivateReason`) }}</span>
                <span class="content">{{ record.reason }}</span>
              </div>
            </div>
            <div class="time">{{ record.operation_time|formatDateTime('YYYY/MM/DD HH:mm') }}</div>
          </div>
        </CollapseContent>
        <div v-if="hasCompletedRecord" class="step-list">
          <CollapseContent
            v-for="(step, stepIndex) in getVisibleQuestionnaireSteps(data)"
            class="step-panel"
            showLeftArrowIcon
            :showRightArrowIcon="false"
            :key="step.id || stepIndex"
            :title="getStepTitle(step, stepIndex)"
          >
            <div class="question-list">
              <template v-for="(question, questionIndex) in getVisibleStepAnswers(step)">
                <div
                  v-if="isRepeatGroup(question)"
                  :key="`repeat-${question.id || questionIndex}`"
                  class="question-item repeat-question-block"
                >
                  <div class="repeat-group">
                    <div v-if="!getSubFields(question).length" class="config-missing">
                      {{ $t('questionnaire.label.configMissing') }}
                      <pre>{{ formatRawAnswer(question) }}</pre>
                    </div>
                    <div v-else-if="!getRepeatRows(question).length" class="empty-text">-</div>
                    <template v-else>
                      <div
                        v-for="(row, rowIndex) in getRepeatRows(question)"
                        :key="rowIndex"
                        class="repeat-row"
                      >
                        <div class="repeat-title">{{ $t('questionnaire.label.entryNo', { number: rowIndex + 1 }) }}</div>
                        <div class="field-grid">
                          <div
                            v-for="field in getSubFields(question)"
                            :key="field.key"
                            class="readonly-field"
                          >
                            <label>{{ getConfigText(field, 'label') }}</label>
                            <div class="readonly-value">{{ getRepeatFieldValue(row, field) }}</div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
                <div
                  v-else
                  :key="`question-${question.id || questionIndex}`"
                  class="question-item"
                >
                  <el-checkbox v-if="isProtocolQuestion(question)" class="question-title consent-question" :value="isProtocolAgreed(question)" disabled>
                    <div class="link-label" :class="hasLink(question) ? 'has-link' : ''" @click="handleQuestionClick(question)">{{ getQuestionTitle(question) }}</div>
                  </el-checkbox>
                  <div v-else-if="isSignature(question)" class="question-title signature-title">
                    <div class="title">{{ getQuestionTitle(question) }}</div>
                    <div class="signature-date-label">{{ $t(`${i18nPrefix}.label.applicationFormDate`) }}</div>
                  </div>
                  <div v-else class="question-title">{{ getQuestionTitle(question) }}</div>
                  <div v-if="isProtocolQuestion(question)" class="readonly-field protocol-content">
                    <div class="readonly-value">{{ getProtocolContent(question) }}</div>
                  </div>
                  <div v-else-if="isDateRange(question)" class="field-grid two-columns">
                    <div class="readonly-field">
                      <label>{{ getDateRangeLabel(question, 'start') }}</label>
                      <div class="readonly-value">{{ getDateRangeValue(question, 'start') }}</div>
                    </div>
                    <div class="readonly-field">
                      <label>{{ getDateRangeLabel(question, 'end') }}</label>
                      <div class="readonly-value">{{ getDateRangeValue(question, 'end') }}</div>
                    </div>
                  </div>
                  <div v-else-if="isSignature(question)" class="readonly-field signature-field">
                    <div v-if="question.answer" class="signature-image">
                      <img v-if="question.answer" :src="question.answer" alt="signature">
                    </div>
                    <div v-if="data.finished_time" class="signature-date">
                      <div class="date">{{ data.finished_time|formatDate }}</div>
                    </div>
                  </div>
                  <div v-else class="readonly-field">
                    <div class="readonly-value">
                      <a v-if="isAttachment(question) && question.answer" :href="question.answer" target="_blank">{{ $t('common.btn.view') }}</a>
                      <template v-else>{{ getDisplayAnswer(question) }}</template>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </CollapseContent>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { QUESTIONNAIRE_QUESTION_TYPE } from '@/constant/questionnaire'
import CollapseContent from './collapseContent.vue'

export default {
  components: {
    CollapseContent
  },
  props: {
    data: { type: Object, default: () => {} },
    candidateName: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate'
    }
  },
  computed: {
    lastInviteOperateRecord () {
      const operationRecords = this.data?.operation_records
      if (!operationRecords?.length) {
        return
      }
      const record = _.chain(operationRecords).filter(record => _.includes(['REACTIVATED', 'INVITED'], record.operation_type)).orderBy('operation_time', ['desc']).first().value()
      return record
    },
    hasCompletedRecord () {
      const operationRecords = this.data?.operation_records
      if (!operationRecords?.length) {
        return false
      }
      const completeRecordIndex = _.findIndex(operationRecords, record => record.operation_type === 'COMPLETED')
      return completeRecordIndex > -1
    }
  },
  methods: {
    getQuestionnaireTitle (item) {
      return item.name || this.$t('questionnaire.label.jobApplication')
    },
    getQuestionTitle (question) {
      const name = question.question && question.question.name
      if (_.isObject(name)) {
        return this.getLocalizedText(name)
      }
      return name || ''
    },
    getQuestionLink (questionInfo) {
      const question = questionInfo.question
      if (question.type !== 'LINK') {
        return
      }
      const config = question.config || {}
      let locale = this.$i18n.locale
      if (locale === 'zh') {
        locale = 'cn'
      } else if (locale === 'hk' || locale === 'tw') {
        locale = 'tc'
      }
      return config[`link_url_${locale}`] || config.link_url_en || config.link_url_cn || config.link_url_tc
    },
    hasLink (questionInfo) {
      const link = this.getQuestionLink(questionInfo)
      if (!link) {
        return false
      }
      return true
    },
    handleQuestionClick (questionInfo) {
      const question = questionInfo.question
      if (question.type !== 'LINK') {
        return
      }
      let link = this.getQuestionLink(questionInfo)
      if (!/^https?:\/\//i.test(link)) {
        link = `https://${link}`
      }
      window.open(link)
    },
    getQuestionnaireSteps (item) {
      if (item.questionnaire_steps && item.questionnaire_steps.length) {
        return item.questionnaire_steps
      }
      return [{
        id: 'default',
        title: this.getQuestionnaireTitle(item),
        questionnaire_answers: item.questionnaire_answers || []
      }]
    },
    getVisibleQuestionnaireSteps (item) {
      return _.filter(this.getQuestionnaireSteps(item), step => {
        return this.getVisibleStepAnswers(step).length > 0
      })
    },
    getVisibleStepAnswers (step) {
      return step.questionnaire_answers || []
    },
    getStepTitle (step, index) {
      const title = step.title
      if (_.isObject(title)) {
        return this.getLocalizedText(title)
      }
      return title || `${this.$t('questionnaire.label.step')} ${index + 1}`
    },
    getAnswerRaw (question) {
      if (question.answer_raw) {
        return question.answer_raw
      }
      if (_.isString(question.answer)) {
        try {
          return JSON.parse(question.answer)
        } catch (e) {
          return null
        }
      }
      return _.isObject(question.answer) ? question.answer : null
    },
    isDateRange (question) {
      return question.question && question.question.type === 'DATE_RANGE'
    },
    isRepeatGroup (question) {
      return question.question && question.question.type === 'REPEAT_GROUP'
    },
    isAttachment (question) {
      return question.question && question.question.type === 'ATTACHMENT'
    },
    isSignature (question) {
      return question.question && question.question.type === 'SIGN'
    },
    isProtocolQuestion (question) {
      return question.question && question.question.type === QUESTIONNAIRE_QUESTION_TYPE.LINK.value
    },
    getProtocolContent (question) {
      const config = (question.question && question.question.config) || {}
      return this.getConfigText(config, 'link_text') || this.getDisplayAnswer(question)
    },
    isProtocolAgreed (question) {
      return question.answer === '1'
    },
    getDateRangeLabel (question, key) {
      const config = question.question.config || {}
      const label = this.getConfigText(config, `${key}_label`)
      if (label) {
        return label
      }
      return key === 'start' ? this.$t('questionnaire.label.startDate') : this.$t('questionnaire.label.endDate')
    },
    getDateRangeValue (question, key) {
      const answer = this.getAnswerRaw(question) || {}
      const value = answer[key] || ''
      return value === 'present' ? this.$t('questionnaire.label.present') : value
    },
    getSubFields (question) {
      const config = question.question.config || {}
      return config.sub_fields || []
    },
    getRepeatRows (question) {
      const answer = this.getAnswerRaw(question) || {}
      return answer.rows || []
    },
    getRepeatFieldValue (row, field) {
      const value = row[field.key]
      if (field.type === 'DATE_RANGE') {
        const start = value ? value.start || '' : ''
        const rawEnd = value ? value.end || '' : ''
        const end = rawEnd || this.$t('questionnaire.label.present')
        if (start && end) {
          return `${start} - ${end}`
        }
        return start || end || ''
      }
      return value || ''
    },
    getConfigText (config, prefix) {
      const locale = this.getConfigLocale()
      return config[`${prefix}_${locale}`] || config[`${prefix}_en`] || config[`${prefix}_cn`] || config[`${prefix}_tc`] || ''
    },
    getConfigLocale () {
      if (this.$i18n.locale === 'en') {
        return 'en'
      }
      if (this.$i18n.locale === 'tw' || this.$i18n.locale === 'hk') {
        return 'tc'
      }
      return 'cn'
    },
    getLocalizedText (value) {
      if (!_.isObject(value)) {
        return value || ''
      }
      const locale = this.getConfigLocale()
      return value[locale] || value.en || value.cn || value.tc || ''
    },
    getDisplayAnswer (question) {
      const answer = question.answer
      if (_.isObject(answer)) {
        return this.getLocalizedText(answer.text || answer) || JSON.stringify(answer)
      }
      return answer || ''
    },
    formatRawAnswer (question) {
      const answer = this.getAnswerRaw(question) || question.answer || ''
      if (_.isString(answer)) {
        return answer
      }
      return JSON.stringify(answer, null, 2)
    },
    getOperationRecordList (item) {
      return _.reverse([].concat(item.operation_records || []))
    },
    getOperationStatusChange (item, record) {
      const operationRecords = item.operation_records || []
      const recordIndex = _.findIndex(operationRecords, { id: record.id })
      const previousRecord = recordIndex > 0 ? operationRecords[recordIndex - 1] : null
      const statusText = this.getOperationStatusText(record.operation_type)
      if (!previousRecord) {
        return statusText
      }
      return `${this.getOperationStatusText(previousRecord.operation_type)} -> ${statusText}`
    },
    getOperationStatusText (operationType) {
      const i18nKeyMap = {
        INVITED: 'jobApplicationStatusInvited',
        REMINDER: 'jobApplicationStatusReminded',
        COMPLETED: 'jobApplicationStatusCompleted',
        REACTIVATED: 'jobApplicationStatusReactivated'
      }
      const i18nKey = i18nKeyMap[operationType]
      return i18nKey ? this.$t(`questionnaire.label.${i18nKey}`) : ''
    }
  }
}
</script>

<style lang="scss" scoped>
.post-applicant-readonly {
  width: 822px;
  margin: 0 auto;

  .application-record {
    border-bottom: 1px solid #ebeef5;

    &:last-child {
      margin-bottom: 0;
      border-bottom: 0;
    }

    .application-title {
      margin-top: 15px;
      margin-bottom: 12px;
      color: #303133;
      font-size: 18px;
      font-weight: bold;
    }

    .application-header {
      display: flex;
      flex-wrap: wrap;
      gap: 16px 32px;
      margin-bottom: 16px;
      color: #606266;
    }

    .meta-label {
    }

    .operation-records {
      color: #606266;
      font-size: 14px;
    }

    .operation-record {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #ebeef5;
      padding: 15px 0;

      &:last-child {
        border-bottom: 0;
        padding-bottom: 0;
      }

      .operation-info {
        .operator {
          margin-bottom: 4px;
          color: #606266;
          font-size: 16px;
          font-weight: bold;
        }

        .candidate,
        .status,
        .reason {
          color: #606266;
        }
      }

      .time {
        flex-shrink: 0;
        margin-left: 20px;
      }
    }

    .config-missing,
    .empty-text {
      color: #909399;
      margin-left: 12px;
    }

    .step-panel {
      margin-bottom: 14px;
      border: 1px solid #ebeef5;

      &:last-child {
        margin-bottom: 0;
      }

      ::v-deep {
        .title-container {
          height: 44px;
          padding: 0 14px;
          background: #fbfbfb;
          border-bottom: 1px solid #ebeef5;
          line-height: 44px;
          font-size: 16px;
        }

        .arrow-icon {
          margin-left: 10px;
          font-size: 14px;
        }

        .collapse-content .content {
          padding: 14px;
        }
      }

      .question-item {
        margin-bottom: 18px;

        .signature-title {
          display: flex;
          align-items: center;
          .title {
            flex: 1;
          }

          .signature-date-label {
            flex: 1;
            margin-left: 20px;
          }
        }
      }

      .question-title {
        margin-bottom: 8px;
        font-weight: 600;
        color: #303133;
      }

      .consent-question {
        ::v-deep {
          .el-checkbox__label {
            color: #303133;
          }
        }
        .has-link {
          color: #0096cc;
        }
      }

      .repeat-question-block {

        .config-missing {
          color: #909399;

          pre {
            white-space: pre-wrap;
            word-break: break-word;
          }
        }

        .repeat-row {
          margin-bottom: 12px;
          padding: 12px;
          border: 1px solid #ebeef5;

          .repeat-title {
            margin-bottom: 10px;
            color: #606266;
            font-weight: 600;
          }
        }
      }

      .field-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(220px, 1fr));
        gap: 12px 20px;

        &.two-columns {
          max-width: 760px;
        }
      }

      .readonly-field {
        label {
          display: block;
          margin-bottom: 4px;
          color: #909399;
          font-size: 13px;
        }
      }

      .signature-field {
        display: flex;
        align-items: center;
        .signature-image {
          display: block;
          flex: 1;
          border: 1px solid #dcdfe6;
          background: #f9fafc;
          height: 64px;
          img {
            display: block;
            width: 220px;
            height: 100%;
            object-fit: contain;
          }
        }

        .signature-date {
          flex: 1;
          border: 1px solid #dcdfe6;
          background: #f9fafc;
          height: 64px;
          margin-left: 20px;
          display: flex;
          align-items: center;
          .date {
            margin-left: 30px;
          }
        }
      }

      .protocol-content {
        .readonly-value {
          white-space: pre-line;
        }

        .protocol-checkbox {
          margin-top: 10px;

          ::v-deep .el-checkbox__label {
            color: #606266;
          }

          ::v-deep .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner {
            background-color: #409eff;
            border-color: #409eff;
          }
        }
      }

      .readonly-value {
        min-height: 34px;
        padding: 7px 10px;
        border: 1px solid #dcdfe6;
        background: #f9fafc;
        color: #303133;
        line-height: 20px;
        word-break: break-word;
      }
    }
  }
}
</style>
